import { Alert, AlertTitle, Button, Dialog, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useSelector } from "react-redux";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import { impersonatedAccessToAllEntitiesSelector } from "../../store/state/user/selectors";

const inspectModeDifferences = [
  "• you see the same pages and actions the user sees, but actions that create or change data are blocked with a notice;",
  '• you can see messages with "Preview" state in the Inbox, users do not see these messages;',
  '• you cannot change "Is Important" and "Is Read" properties of messages in the Inbox, users can change them;',
  "• you cannot update the photo in user's profile, users can update it;",
  '• number of unread messages and list of message tags in the left panel of the Inbox do not include messages with "Preview" state;',
  "• you cannot apply Inbox grid column resizing.",
].join("\n");

interface Props {
  open: boolean;
  onClose: () => void;
}

const ImpersonationHelpDialog = ({ open, onClose }: Props) => {
  const impersonatedAccessToAllEntities = useSelector(impersonatedAccessToAllEntitiesSelector);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Help</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2}>
          {!impersonatedAccessToAllEntities && (
            <Alert severity="warning">
              <AlertTitle>Entity-Level Access Restricted</AlertTitle>
              <Stack spacing={0.5}>
                <Typography>
                  You are impersonating a portal contact, but your access is restricted by entity-level permissions.
                  This means some data visible to the portal contact - such as documents or fund data — may not be
                  available to you.
                </Typography>
                <Typography>To understand how this works, see the Entity-Level Access article.</Typography>
                <Typography>
                  If you&apos;re unsure about this restriction, reach out to your Organization Owner for more details.
                </Typography>
              </Stack>
            </Alert>
          )}
          <Typography variant="subtitle1">Inspect Mode</Typography>
          <Stack spacing={1}>
            <Typography color="textSecondary">
              You are inspecting the Investor Portal on behalf of users. This mode has the following differences from
              the users&apos; live view:
            </Typography>
            <Typography color="textSecondary" sx={{ whiteSpace: "pre-line" }}>
              {inspectModeDifferences}
            </Typography>
          </Stack>
        </Stack>
      </DialogContent>
      <DialogButtons>
        <Button variant="contained" onClick={onClose}>
          Close
        </Button>
      </DialogButtons>
    </Dialog>
  );
};

export default ImpersonationHelpDialog;
